<script lang="ts">
	//Services
	import { format } from '../../../../services/i18n';
	import { gen2Request } from '../../../../services/gen_2_http_reqs';
	//Helpers
	import {
		get_gen_2_component_channel_structure,
		get_gen_2_component_from_category,
		get_mode,
		real_id,
		get_device_component_index,
	} from '../../../../helpers/interface_helpers';
	//Constants
	import { DEVICE_MAX_CURRENT_DEFAULTS, PROFILE_DEVICES } from '../../../../constants';
	//Stores
		import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	//Types
	import type { SettingRequest } from '../../../../types/requests/settings/setting';
	//Design system components
	import { Button, Input, toast, Text, Alert } from '@Shelly/shelly-design-system';
	import { capFirstChar, makeNumbersPrimaryAndBold } from 'src/helpers/utilities';
	import { navigateToSetting } from 'src/stores/ui/navigate-to';
	import { restrictDecimalNumberInput } from 'src/helpers/number_input_helpers';
	import { getContext, onDestroy } from 'svelte';
	import type { Writable } from 'svelte/store';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';

	export let device;

	const id = real_id(device.id);
	const deviceType = get_gen_2_component_from_category(device);
	const structure = get_gen_2_component_channel_structure(device);
	const index = get_device_component_index(device);

	$: statusStore = getDeviceStatus(id);
	$: settingsStore = getDeviceSettings(id);
	$: mode = get_mode(device, $statusStore, $settingsStore);
	$: isNotCalibrated =
		$statusStore?.[structure]?.flags?.includes('uncalibrated') ||
		$statusStore?.[structure]?.flags?.includes('no_load'); //non calibrated light doesn't have max protection

	let settings = $settingsStore;

	const unit = 'A';
	// const prop = $format('prop_cur_meter', { default: 'the current' });

	let value = $settingsStore[structure]?.current_limit;
	// let cacheValue = value;

	let loading = false;
	$: error =
		+value < min || +value > max
			? $format('error_out_of_range', {
					default: `Value ${value}${unit} is out of range. Please provide a value within the limits. (${min}${unit} — ${max}${unit})`,
					values: { value, max, min, unit },
				})
			: '';

	// $: info = $format('info_value_range', {
	// 	default: `Configure Shelly device to turn off when ${prop} reaches ${value} ${unit}. Max: ${max} ${unit}.`,
	// 	values: { prop, value, max, unit }
	// });

	const min = 0;
	$: max =
		DEVICE_MAX_CURRENT_DEFAULTS[
			device.type + (PROFILE_DEVICES.includes(device.type) && mode === 'light' ? 'L' : '') //when device has profiles and it's gen2 on light profile the protection is 4A otherwise limits are 10A. For the rest of devices it's inside constants
		];
	const setCurrentLimit = async () => {
		if (value === '') {
			value = max;
		}
		if (value < min || value > max) {
			return toast.open({
				type: 'danger',
				title: $format('max_current_prot_limit_err_toast'),
			});
		}

		const params = {
			id: index,
			config: {
				power_limit: settings[`${deviceType}:${index}`].power_limit,
				voltage_limit: settings[`${deviceType}:${index}`].voltage_limit,
				current_limit: +value,
			},
		};
		// if (cacheValue !== value) {
		loading = true;
		const res: SettingRequest = await gen2Request(device, `${deviceType}.setconfig`, params);
		if (res.isok) {
			settings[`${deviceType}:${index}`].current_limit = value;
			devices_settings.update_devices_settings(id, settings);

			toast.open({
				type: 'success',
				title: $format('max_current_prot_save_scc_toast', {
					values: { value: value },
				}),
			});
			// cacheValue = value;
		} else {
			// value = cacheValue;
			toast.open({
				type: 'danger',
				title: $format('max_current_prot_save_err_toast'),
			});
		}
		loading = false;
		// }
	};

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	$: if (stickyBottomAction) {
		if ($showNewLayout && !isNotCalibrated) {
			stickyBottomAction.set({ onClick: setCurrentLimit, loading, disabled: loading });
		} else {
			stickyBottomAction.set(null);
		}
	}

	onDestroy(() => {
		stickyBottomAction?.set(null);
	});
</script>

{#if isNotCalibrated}
	<Alert
		class="mb-s"
		heading={$format('alert_heading_non_calibrated_dimmer', {
			default: `Non calibrated channel.`,
		})}
		message={$format('description_dimmer_calibration', {
			default:
				"Calibrate current dimmer's channel. Note: During calibration you can't calibrate other channel. During calibration don't change current channel state.",
		})}
		isHeadingBold
		cta={{
			title: capFirstChar(
				$format('button_calibrate', {
					default: 'calibrate',
				}),
			),
		}}
		on:cta_click={() => {
			navigateToSetting(device.id, 'settings', 'calibration');
			// activeDeviceTabIndex = 14;
		}}
	/>
{:else}
	<Text styleType="controls-small" class="mb-s">
		{@html makeNumbersPrimaryAndBold(
			`${$format('max_current_prot_description', {
				values: { value },
				default: `Configure Shelly device to turn off when current reaches ${value} A.`,
			})}`,
		)}
	</Text>

	<Input
		on:input={(e) => restrictDecimalNumberInput(e, 2)}
		size="m"
		bind:value
		type={'number'}
		{min}
		{max}
		info={$format('range_min_max_ampere_value', {
			default: 'Range: {min} A - {max} A.',
			values: { min, max },
		})}
		{error}
		step={Number.isInteger(max) ? 1 : 0.01}
		on:enter={setCurrentLimit}
		aria-label={$format('max_current_prot_description', {
			default: 'Max current protection',
		})}
		id="input-gjojimw"
	/>
	{#if !$showNewLayout}
		<Button
			class="mt-s"
			{loading}
			disabled={loading}
			size="m"
			width="full"
			on:click={setCurrentLimit}
		>
			{$format('button_save')}
		</Button>
	{/if}
{/if}
